import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { isTruthy } from "@remi/ts-utils";

import { useEffect } from "../hooks/useEffect";
import { cn } from "../lib/utils";
import { RemiButton, type RemiButtonSize, type RemiButtonVariant } from "./RemiButton";

export type AlertVariants = "secondary" | "accent" | "destructive" | "highlight";
const DEFAULT_VARIANT: AlertVariants = "secondary";
type AlertVariantDefinitions = Record<AlertVariants, string>;

const ALERT_EXIT_MS = 150;

const alertVariants = cva(
  "flex w-full flex-row rounded-lg border p-3 transition-[opacity,scale] duration-150 ease-out-strong",
  {
    variants: {
      variant: {
        secondary: "border-border-primary bg-background-secondary",
        accent: "border-border-accent bg-background-accentLight",
        destructive: "border-border-destructive bg-background-destructiveLight",
        highlight: "border-[rgb(var(--colors-text-yellow))] bg-background-highlight dark:bg-background-orangeLight",
      } satisfies AlertVariantDefinitions,
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
    },
  },
);

const alertHeaderVariants = cva("pb-1 font-medium text-xs leading-none tracking-tight", {
  variants: {
    variant: {
      secondary: "text-text-primary",
      accent: "text-text-primary",
      destructive: "text-text-primary dark:text-text-destructive",
      highlight: "text-text-primary dark:text-text-yellow",
    } satisfies AlertVariantDefinitions,
  },
  defaultVariants: {
    variant: DEFAULT_VARIANT,
  },
});

const alertIconVariants = cva("pr-2", {
  variants: {
    variant: {
      secondary: "text-icon-secondary",
      accent: "text-icon-accent",
      destructive: "text-icon-destructive",
      highlight: "text-icon-yellow",
    } satisfies AlertVariantDefinitions,
  },
  defaultVariants: {
    variant: DEFAULT_VARIANT,
  },
});

const buttonVariants: Record<AlertVariants, RemiButtonVariant> = {
  secondary: "secondary",
  accent: "primary",
  destructive: "destructive",
  highlight: "secondary",
};

interface AlertProps {
  variant?: VariantProps<typeof alertVariants>["variant"];
  variantOverride?: RemiButtonVariant;
  title?: string;
  titleClassName?: string;
  description?: string;
  descriptionClassName?: string;
  icon?: React.ReactNode;
  wrap?: boolean;
  centerContent?: boolean;
  hasButtons?: boolean;
  children?: React.ReactNode;
  ignoreButton?: {
    label?: string;
    hidden?: boolean;
    variantOverride?: RemiButtonVariant;
    onIgnore?: () => void;
    onClickOverride?: () => void;
  };
  acceptButton?: {
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    size?: RemiButtonSize;
    variantOverride?: RemiButtonVariant;
    onClick?: () => void;
  };
}

/**
 * @example
 * ```
 *   <RemiAlert
 *     variant="destructive"
 *     title="Action required: schedule before accepting"
 *     description="This job has extra sensitivity to schedule. Before ..."
 *   />
 * ```
 */
const RemiAlert = ({
  ref,
  className,
  title,
  titleClassName,
  description,
  descriptionClassName,
  variant,
  icon,
  hasButtons = true,
  ignoreButton,
  acceptButton,
  wrap = true,
  centerContent = false,
  children,
  ...props
}: AlertProps &
  React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & { ref?: React.Ref<HTMLDivElement> }) => {
  const [showAlert, setShowAlert] = React.useState(true);
  const [leaving, setLeaving] = React.useState(false);

  useEffect(() => {
    if (!leaving) {
      return;
    }
    const timeout = setTimeout(() => setShowAlert(false), ALERT_EXIT_MS);
    return () => clearTimeout(timeout);
  }, [leaving]);

  const hideAlertAndIgnoreOnClickFn = () => {
    setLeaving(true);
    ignoreButton?.onIgnore?.();
  };

  return showAlert ? (
    <div
      ref={ref}
      role="alert"
      className={cn(
        alertVariants({ variant }),
        leaving && "scale-[0.97] opacity-0",
        title !== undefined && "pt-4",
        !wrap && title === undefined && "items-center",
        centerContent && "justify-center",
        className,
      )}
      {...props}
    >
      {isTruthy(icon) && <div className={alertIconVariants({ variant })}>{icon}</div>}
      <div className={cn("flex flex-col", centerContent ? "" : "w-full")}>
        {title && (
          <RemiAlertTitle variant={variant} className={titleClassName}>
            {title}
          </RemiAlertTitle>
        )}
        <div className={cn("flex w-full space-y-1", wrap ? "flex-col" : "flex-row items-center justify-between")}>
          {description && (
            <RemiAlertDescription
              className={cn(title === undefined && "text-text-primary leading-none", descriptionClassName)}
            >
              {description}
            </RemiAlertDescription>
          )}
          {children}
          {hasButtons && (
            <div className="flex justify-end space-x-1">
              {ignoreButton?.hidden !== true && (
                <RemiButton
                  onClick={ignoreButton?.onClickOverride ? ignoreButton.onClickOverride : hideAlertAndIgnoreOnClickFn}
                  variant={ignoreButton?.variantOverride}
                  muted={ignoreButton?.variantOverride === undefined}
                >
                  {ignoreButton?.label ?? "Ignore"}
                </RemiButton>
              )}
              <RemiButton
                onClick={acceptButton?.onClick ? acceptButton.onClick : undefined}
                variant={acceptButton?.variantOverride ?? buttonVariants[variant ?? DEFAULT_VARIANT]}
                size={acceptButton?.size}
                disabled={acceptButton?.disabled ?? false}
                loading={acceptButton?.loading ?? false}
              >
                {acceptButton?.label ?? "Accept"}
              </RemiButton>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;
};
RemiAlert.displayName = "RemiAlert";

const RemiAlertTitle = ({
  ref,
  className,
  variant,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof alertHeaderVariants> & { ref?: React.Ref<HTMLParagraphElement> }) => (
  <div ref={ref} className={cn(alertHeaderVariants({ variant }), className)} {...props} />
);
RemiAlertTitle.displayName = "RemiAlertTitle";

const RemiAlertDescription = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & { ref?: React.Ref<HTMLParagraphElement> }) => (
  <div ref={ref} className={cn("text-text-secondary text-xs", className)} {...props} />
);
RemiAlertDescription.displayName = "RemiAlertDescription";

export { RemiAlert, RemiAlertTitle };
